import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ChangePasswordModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oldError, setOldError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  function reset() {
    setOldPassword(''); setNewPassword(''); setConfirm('');
    setError(null); setOldError(null); setSuccess(false);
  }

  function close() { reset(); onClose(); }

  async function submit() {
    setError(null); setOldError(null);
    if (newPassword !== confirm) { setError(t('password.mismatch')); return; }
    setSubmitting(true);
    try {
      await authAPI.changeAdminPassword(oldPassword, newPassword);
      setSuccess(true);
      window.setTimeout(close, 1200);
    } catch (e: any) {
      const msg = e?.message ?? 'Failed';
      if (/current password/i.test(msg)) setOldError(t('password.wrongCurrent'));
      else setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={close}>
      <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-warm-gray-900 dark:text-white mb-4">{t('password.changeTitle')}</h3>

        {success && (
          <div className="mb-3 px-3 py-2 rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-sm">
            {t('password.success')}
          </div>
        )}
        {error && (
          <div className="mb-3 px-3 py-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="block text-xs font-semibold text-warm-gray-600 dark:text-warm-gray-400 mb-1">{t('password.current')}</label>
          <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} className={inputCls} autoFocus />
          {oldError && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{oldError}</p>}
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold text-warm-gray-600 dark:text-warm-gray-400 mb-1">{t('password.new')}</label>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className={inputCls} />
          <p className="text-xs text-warm-gray-500 mt-1">{t('password.rules')}</p>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-semibold text-warm-gray-600 dark:text-warm-gray-400 mb-1">{t('password.confirm')}</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className={inputCls} />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={close} className="px-4 py-2 rounded border border-warm-gray-300 dark:border-warm-gray-600 text-warm-gray-700 dark:text-warm-gray-300 text-sm">
            {t('common.cancel')}
          </button>
          <button
            onClick={() => void submit()}
            disabled={submitting || success || !oldPassword || !newPassword || !confirm}
            className="px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            {submitting ? t('password.submitting') : t('password.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 rounded border border-warm-gray-300 dark:border-warm-gray-600 bg-white dark:bg-warm-gray-900 text-warm-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
